import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Clock,
  CheckCircle2,
  MessageSquare,
  Send,
  FileText,
  FileSignature,
  TrendingUp,
  Phone,
  Building,
  UserCheck,
  AlertTriangle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProjectDetailV2() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const chatEndRef = useRef(null);
  
  const [project, setProject] = useState(null);
  const [contract, setContract] = useState(null);
  const [teamContract, setTeamContract] = useState(null);
  const [applications, setApplications] = useState([]);
  const [dailyProgress, setDailyProgress] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  
  // Contact chat
  const [contactMessage, setContactMessage] = useState("");
  const [sendingContactMessage, setSendingContactMessage] = useState(false);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  useEffect(() => {
    // Poll for messages
    const interval = setInterval(() => {
      if (activeTab === "contact") {
        fetchContactMessages();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab, projectId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [contactMessages]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      // Fetch project
      const projectRes = await axios.get(`${API}/admin/projects/${projectId}/review`);
      setProject(projectRes.data);
      
      // Fetch contract
      try {
        const contractRes = await axios.get(`${API}/admin/contracts/${projectId}`);
        setContract(contractRes.data);
      } catch (e) {
        // No contract yet
      }
      
      // Fetch team contract
      try {
        const teamContractRes = await axios.get(`${API}/admin/team-contracts/${projectId}`);
        setTeamContract(teamContractRes.data);
      } catch (e) {
        // No team contract yet
      }
      
      // Fetch applications
      try {
        const appsRes = await axios.get(`${API}/admin/projects/${projectId}/applications`);
        setApplications(appsRes.data || []);
      } catch (e) {
        setApplications([]);
      }
      
      // Fetch daily progress
      try {
        const progressRes = await axios.get(`${API}/admin/projects/${projectId}/progress`);
        setDailyProgress(progressRes.data || []);
      } catch (e) {
        setDailyProgress([]);
      }
      
      // Fetch contact messages
      fetchContactMessages();
      
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/admin/projects");
    } finally {
      setLoading(false);
    }
  };

  const fetchContactMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/projects/${projectId}/contact-messages`);
      setContactMessages(res.data || []);
    } catch (e) {
      // Endpoint might not exist yet
    }
  };

  const handleSendContactMessage = async (e) => {
    e.preventDefault();
    if (!contactMessage.trim()) return;
    
    setSendingContactMessage(true);
    try {
      await axios.post(`${API}/admin/projects/${projectId}/contact`, {
        message: contactMessage.trim()
      });
      setContactMessage("");
      fetchContactMessages();
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingContactMessage(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400" },
      pending_approval: { label: "Pending Approval", color: "bg-yellow-500/20 text-yellow-400" },
      negotiating: { label: "Negotiating", color: "bg-blue-500/20 text-blue-400" },
      live: { label: "Live", color: "bg-green-500/20 text-green-400" },
      team_assigned: { label: "Team Assigned", color: "bg-purple-500/20 text-purple-400" },
      active: { label: "Active", color: "bg-emerald-500/20 text-emerald-400" },
      in_progress: { label: "In Progress", color: "bg-cyan-500/20 text-cyan-400" },
      completed: { label: "Completed", color: "bg-green-500/20 text-green-400" },
      cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-400" },
    };
    return configs[status] || { label: status, color: "bg-gray-500/20 text-gray-400" };
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Project Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Project Details">
        <div className="text-center py-12">
          <Building className="w-16 h-16 mx-auto mb-4 text-[#475569]" />
          <p className="text-[#94A3B8]">Project not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const statusConfig = getStatusConfig(project.status);
  const showProgressTab = ["team_assigned", "active", "in_progress", "completed"].includes(project.status);
  const showContactTab = ["team_assigned", "active", "in_progress", "completed"].includes(project.status);
  const showApplicationsTab = ["live", "team_assigned", "active", "in_progress", "completed"].includes(project.status);

  return (
    <DashboardLayout navItems={adminNavItems} title="Project Details">
      <div className="space-y-6" data-testid="admin-project-detail">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10" 
              onClick={() => navigate("/admin/projects")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-2xl font-['Unbounded'] font-bold text-white">{project.title}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                <span className="text-[#94A3B8] text-sm">
                  Created on {new Date(project.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1 flex-wrap h-auto gap-1">
            <TabsTrigger value="details" className="data-[state=active]:bg-[#23F7DD] data-[state=active]:text-[#050505]">
              <FileText className="w-4 h-4 mr-2" />Details
            </TabsTrigger>
            
            <TabsTrigger value="contract" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <FileSignature className="w-4 h-4 mr-2" />Contract
            </TabsTrigger>
            
            {showApplicationsTab && (
              <TabsTrigger value="applications" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
                <UserCheck className="w-4 h-4 mr-2" />Applications
                {applications.length > 0 && <Badge className="ml-2 bg-indigo-600">{applications.length}</Badge>}
              </TabsTrigger>
            )}
            
            {showProgressTab && (
              <TabsTrigger value="progress" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                <TrendingUp className="w-4 h-4 mr-2" />Daily Progress
              </TabsTrigger>
            )}
            
            {showContactTab && (
              <TabsTrigger value="contact" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
                <Phone className="w-4 h-4 mr-2" />Contact Team
              </TabsTrigger>
            )}
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#23F7DD]" />Project Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#94A3B8]">{project.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                    <div>
                      <p className="text-xs text-[#94A3B8]">Location</p>
                      <p className="text-white flex items-center gap-1">
                        <MapPin className="w-4 h-4 text-[#23F7DD]" />
                        {project.location_city}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">Type</p>
                      <p className="text-white capitalize">{project.project_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">Crew Size</p>
                      <p className="text-white flex items-center gap-1">
                        <Users className="w-4 h-4 text-[#23F7DD]" />
                        {project.crew_size || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">Start Date</p>
                      <p className="text-white flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-[#23F7DD]" />
                        {project.start_date ? new Date(project.start_date).toLocaleDateString() : "Flexible"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Budget Info */}
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#23F7DD]" />Budget Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-[#94A3B8]">Proposed Budget</p>
                    <p className="text-2xl font-bold text-white">{project.proposed_budget?.toLocaleString() || "N/A"} NOK</p>
                  </div>
                  {project.approved_budget && (
                    <div className="p-4 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg">
                      <p className="text-sm text-[#94A3B8]">Approved Budget</p>
                      <p className="text-2xl font-bold text-[#23F7DD]">{project.approved_budget?.toLocaleString()} NOK</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contract Tab */}
          <TabsContent value="contract" className="mt-6 space-y-6">
            {/* Platform Contract */}
            {contract && (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded']">Platform Contract</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-[#94A3B8]">Total Amount</p>
                      <p className="text-lg font-bold text-white">{contract.total_amount?.toLocaleString()} NOK</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-[#94A3B8]">Platform Fee</p>
                      <p className="text-lg font-bold text-[#23F7DD]">{contract.platform_fee || 10}%</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-[#94A3B8]">Net for Workers</p>
                      <p className="text-lg font-bold text-green-400">{contract.net_amount_for_workers?.toLocaleString()} NOK</p>
                    </div>
                    <div className="p-3 bg-white/5 rounded-lg">
                      <p className="text-xs text-[#94A3B8]">Status</p>
                      <Badge className={contract.signed ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                        {contract.signed ? "Signed" : "Pending"}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team Contract */}
            {teamContract && (
              <Card className="bg-[#0A0A0A] border-cyan-500/30">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <FileSignature className="w-5 h-5 text-cyan-400" />Team Contract
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`p-4 rounded-lg ${teamContract.all_signed ? "bg-green-500/10 border border-green-500/30" : "bg-yellow-500/10 border border-yellow-500/30"}`}>
                    <div className="flex items-center gap-2">
                      {teamContract.all_signed ? (
                        <><CheckCircle2 className="w-5 h-5 text-green-400" /><span className="text-green-400 font-medium">All Signatures Complete</span></>
                      ) : (
                        <><AlertTriangle className="w-5 h-5 text-yellow-400" /><span className="text-yellow-400 font-medium">Awaiting Signatures</span></>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-black/30 rounded-lg text-center">
                      <p className="text-xs text-[#94A3B8]">Net Amount for Team</p>
                      <p className="text-xl font-bold text-[#23F7DD]">{teamContract.net_amount?.toLocaleString()} NOK</p>
                    </div>
                    <div className="p-4 bg-black/30 rounded-lg text-center">
                      <p className="text-xs text-[#94A3B8]">Team Size</p>
                      <p className="text-xl font-bold text-white">{teamContract.signatures?.length || 1}</p>
                    </div>
                  </div>

                  {teamContract.signatures && (
                    <div>
                      <p className="text-sm text-[#94A3B8] mb-2">Team Member Signatures:</p>
                      <div className="space-y-2">
                        {teamContract.signatures.map((sig, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-black/20 rounded">
                            <span className="text-white">{sig.name}</span>
                            <Badge className={sig.signed ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                              {sig.signed ? "Signed" : "Pending"}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {!contract && !teamContract && (
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardContent className="p-12 text-center">
                  <FileSignature className="w-12 h-12 mx-auto mb-4 text-[#94A3B8] opacity-50" />
                  <p className="text-[#94A3B8]">No contracts available yet</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Applications Tab */}
          {showApplicationsTab && (
            <TabsContent value="applications" className="mt-6">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-[#23F7DD]" />Team Applications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {applications.length === 0 ? (
                    <div className="text-center py-8 text-[#94A3B8]">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No applications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.map((app) => (
                        <div key={app.application_id} className="p-4 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <p className="text-white font-medium">{app.team_leader_name}</p>
                              <p className="text-sm text-[#94A3B8]">Team Size: {app.team_size}</p>
                            </div>
                            <Badge className={
                              app.status === "accepted" ? "bg-green-500/20 text-green-400" :
                              app.status === "rejected" ? "bg-red-500/20 text-red-400" :
                              "bg-yellow-500/20 text-yellow-400"
                            }>
                              {app.status}
                            </Badge>
                          </div>
                          {app.message && (
                            <p className="text-sm text-[#94A3B8] mt-2">{app.message}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Daily Progress Tab */}
          {showProgressTab && (
            <TabsContent value="progress" className="mt-6">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-400" />Daily Progress Updates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dailyProgress.length === 0 ? (
                    <div className="text-center py-8 text-[#94A3B8]">
                      <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No progress updates yet</p>
                      <p className="text-sm">The team will post updates here once work begins</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {dailyProgress.map((entry) => (
                        <div key={entry.progress_id} className="p-4 bg-white/5 rounded-lg border-l-4 border-orange-400">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="text-white font-medium">{entry.posted_by_name}</p>
                              <p className="text-xs text-[#94A3B8]">
                                {new Date(entry.created_at).toLocaleString()}
                              </p>
                            </div>
                            {entry.hours_worked && (
                              <Badge className="bg-orange-500/20 text-orange-400">
                                <Clock className="w-3 h-3 mr-1" />{entry.hours_worked}h
                              </Badge>
                            )}
                          </div>
                          <p className="text-[#94A3B8]">{entry.description}</p>
                          {entry.notes && (
                            <p className="text-sm text-[#94A3B8] mt-2 italic">Note: {entry.notes}</p>
                          )}
                          {entry.photos?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {entry.photos.map((url, idx) => (
                                <a 
                                  key={idx}
                                  href={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img 
                                    src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
                                    alt={`Progress ${idx + 1}`}
                                    className="w-24 h-24 object-cover rounded-lg hover:opacity-80 transition-opacity"
                                  />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Contact Tab */}
          {showContactTab && (
            <TabsContent value="contact" className="mt-6">
              <Card className="bg-[#0A0A0A] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-400" />Contact Team
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Messages */}
                  <div className="h-96 overflow-y-auto mb-4 space-y-3 p-4 bg-black/30 rounded-lg">
                    {contactMessages.length === 0 ? (
                      <div className="flex items-center justify-center h-full text-[#94A3B8]">
                        <div className="text-center">
                          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No messages yet</p>
                          <p className="text-sm">Start a conversation with the team</p>
                        </div>
                      </div>
                    ) : (
                      contactMessages.map((msg) => {
                        const isAdmin = msg.sender_role === "platform_admin" || msg.sender_id === user?.user_id;
                        return (
                          <div key={msg.message_id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[70%] p-3 rounded-lg ${
                              isAdmin 
                                ? "bg-[#23F7DD] text-[#050505]" 
                                : "bg-white/10 text-white"
                            }`}>
                              <p className="text-xs opacity-70 mb-1">
                                {isAdmin ? "You (Admin)" : msg.sender_name || "Team"}
                              </p>
                              <p>{msg.message}</p>
                              <p className="text-xs opacity-50 mt-1">
                                {new Date(msg.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={chatEndRef} />
                  </div>
                  
                  {/* Send Message Form */}
                  <form onSubmit={handleSendContactMessage} className="flex gap-2">
                    <Input
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 bg-black/50 border-white/10 text-white"
                    />
                    <Button 
                      type="submit" 
                      disabled={sendingContactMessage || !contactMessage.trim()}
                      className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
